import type { FC } from "react";
import type {
  CompanyFilters,
  FilterChip,
  FilterKey,
  FilterLookups,
} from "../types";
import { employeesModule } from "./employees";
import { revenueModule } from "./revenue";
import { companyTypeModule } from "./companyType";
import { targetOrganizationsModule } from "./targetOrganizations";
import { geographyModule } from "./geography";
import { globalPresenceModule } from "./globalPresence";
import { rankingsModule } from "./rankings";
import { promotionDateModule } from "./promotionDate";
import { financingEventsModule } from "./financingEvents";
import { industryModule } from "./industry";
import { productCategoriesModule } from "./productCategories";
import { productAttributesModule } from "./productAttributes";
import { productsModule } from "./products";
import { vendorsModule } from "./vendors";
import { itSpendModule } from "./itSpend";
import { uploadedAccountsModule } from "./uploadedAccounts";
import { knownCompaniesModule } from "./knownCompanies";
import { intentModule } from "./intent";
import { fitScoreModule } from "./fitScore";
import { intentScoreModule } from "./intentScore";
import { contactModeModule } from "./contactMode";
import { contactJobSeniorityModule } from "./contactJobSeniority";
import { contactJobFunctionModule } from "./contactJobFunction";
import { contactJobTitleModule } from "./contactJobTitle";
import { contactLocationModule } from "./contactLocation";
import { contactTimeInRoleModule } from "./contactTimeInRole";
import { buyerIntentProductsModule } from "./buyerIntentProducts";
import { buyerIntentVendorsModule } from "./buyerIntentVendors";
import { buyerIntentCategoriesModule } from "./buyerIntentCategories";
import { buyerIntentAttributesModule } from "./buyerIntentAttributes";
import { modelScoreModule } from "./modelScore";
import { companyConfigModule } from "./companyConfig";

/**
 * Contract for filter modules in the company-filter library.
 *
 * Each module encapsulates a specific filter type (geography, industry, etc.)
 * and provides a consistent interface for:
 * - UI rendering (Component)
 * - State management (Slice, defaultSlice)
 * - API serialization (toCompanyFilters, fromCompanyFilters)
 * - Chip generation (getChips, getCount)
 *
 * ## Lifecycle
 * 1. On mount: `defaultSlice` initializes the module state
 * 2. On hydration: `fromCompanyFilters(apiFilters)` populates slice from saved data
 * 3. On user interaction: Component updates slice via `setSlice`
 * 4. On query: `toCompanyFilters(slice)` serializes slice to API format
 * 5. On chip render: `getChips(slice, setSlice, lookups)` generates filter chips
 *
 * ## Creating a new module
 * 1. Define slice type and default in `slice.ts`
 * 2. Implement mappers in `mapper.ts`
 * 3. Implement chip logic in `chips.tsx`
 * 4. Create filter component in `*Filter.tsx`
 * 5. Export module definition in `index.ts`
 * 6. Register in `modules/index.tsx` registry
 *
 * @template Slice - The module's state shape
 * @template Lookups - Optional lookup data for resolving IDs to names
 */
export type FilterModule<Slice, Lookups = unknown> = {
  /** Unique identifier matching a FilterKey. Used for routing and chip association. */
  key: FilterKey;

  /** Initial state for the module. Used on mount and reset. */
  defaultSlice: Slice;

  /** React component rendered in the filter panel when this filter is active. */
  Component: FC;

  /** Converts internal slice to GraphQL CompanyFilters format for API queries. */
  toCompanyFilters: (slice: Slice) => Partial<CompanyFilters>;

  /** Hydrates internal slice from saved/loaded CompanyFilters (e.g., from segment). */
  fromCompanyFilters: (filters: CompanyFilters) => Slice;

  /**
   * Generates filter chips for the chips bar.
   * @param slice - Current module state
   * @param setSlice - Callback to update state (used for chip removal)
   * @param lookups - Optional lookup data for resolving IDs to display names
   */
  getChips: (
    slice: Slice,
    setSlice: (slice: Slice) => void,
    lookups?: Lookups
  ) => FilterChip[];

  /** Returns count of active filters for sidebar badge. */
  getCount: (slice: Slice) => number;
};

export const modules = {
  targetOrganizations: targetOrganizationsModule,
  uploadedAccounts: uploadedAccountsModule,
  knownCompanies: knownCompaniesModule,
  companyType: companyTypeModule,
  geography: geographyModule,
  globalPresence: globalPresenceModule,
  rankings: rankingsModule,
  promotionDate: promotionDateModule,
  employees: employeesModule,
  revenue: revenueModule,
  industry: industryModule,
  productCategories: productCategoriesModule,
  productAttributes: productAttributesModule,
  products: productsModule,
  vendors: vendorsModule,
  itSpend: itSpendModule,
  intent: intentModule,
  fitScore: fitScoreModule,
  intentScore: intentScoreModule,
  contactMode: contactModeModule,
  contactJobSeniority: contactJobSeniorityModule,
  contactJobFunction: contactJobFunctionModule,
  contactJobTitle: contactJobTitleModule,
  contactLocation: contactLocationModule,
  contactTimeInRole: contactTimeInRoleModule,
  buyerIntentProducts: buyerIntentProductsModule,
  buyerIntentVendors: buyerIntentVendorsModule,
  buyerIntentCategories: buyerIntentCategoriesModule,
  buyerIntentAttributes: buyerIntentAttributesModule,
  modelScore: modelScoreModule,
  companyConfig: companyConfigModule,
  financingEvents: financingEventsModule,
} as const;

export type ModuleKey = keyof typeof modules;

export type ModulesSlices = {
  [K in ModuleKey]: (typeof modules)[K]["defaultSlice"];
};

export type ModulesLookups = {
  [K in ModuleKey]: Parameters<(typeof modules)[K]["getChips"]>[2];
};

export const moduleKeys = Object.keys(modules) as ModuleKey[];

/**
 * Type guard for runtime strings coming from the sidebar/filter metadata.
 * Keeps key→module typing intact once validated.
 */
export function isModuleKey(key: string): key is ModuleKey {
  return key in modules;
}

/**
 * Centralized helpers for iterating the module registry without leaking `as any`
 * into consumers.
 *
 * TypeScript can't preserve the key→slice mapping through dynamic iteration, so
 * we intentionally erase the slice type to a union at this boundary.
 */
type SetSliceFn = <K extends ModuleKey>(
  key: K,
  slice: ModulesSlices[K]
) => void;

export function getModuleChips(
  key: ModuleKey,
  slices: ModulesSlices,
  setSlice: SetSliceFn,
  lookups?: FilterLookups
): FilterChip[] {
  const module = modules[key] as unknown as FilterModule<
    ModulesSlices[ModuleKey],
    ModulesLookups[ModuleKey]
  >;
  const slice = slices[key] as ModulesSlices[ModuleKey];
  const setModuleSlice = (nextSlice: ModulesSlices[ModuleKey]) => {
    (
      setSlice as unknown as (k: ModuleKey, s: ModulesSlices[ModuleKey]) => void
    )(key, nextSlice);
  };
  const moduleLookups = lookups?.[key] as unknown as ModulesLookups[ModuleKey];
  return module.getChips(slice, setModuleSlice, moduleLookups);
}

export function getModuleCount(key: ModuleKey, slices: ModulesSlices): number {
  const module = modules[key] as unknown as FilterModule<
    ModulesSlices[ModuleKey]
  >;
  const slice = slices[key] as ModulesSlices[ModuleKey];
  return module.getCount(slice);
}

export function collectModuleChips(
  slices: ModulesSlices,
  setSlice: SetSliceFn,
  lookups?: FilterLookups
): FilterChip[] {
  const chips: FilterChip[] = [];
  moduleKeys.forEach((key) => {
    chips.push(...getModuleChips(key, slices, setSlice, lookups));
  });
  return chips;
}
